<template>
  <el-dialog
    :visible.sync="dialogVisible"
    custom-class="kr-toolkit-browser"
    width="640px"
    append-to-body
    :close-on-click-modal="true"
  >
    <div slot="title" class="kr-toolkit-browser__header">
      <div class="aq-form-label">CONNECT A SOURCE</div>
      <div class="kr-toolkit-browser__heading">Browse 250+ apps</div>
    </div>

    <div class="kr-toolkit-browser__body">
      <el-input
        v-model="search"
        prefix-icon="el-icon-search"
        placeholder="Search apps. Try HubSpot, Mixpanel, GitHub, PostHog, anything Composio supports."
        size="small"
        clearable
        ref="searchInput"
      />

      <!-- Categories only render in the default (no-search) view. When the
           user starts typing we collapse them to keep the focus on results
           and avoid implying that category filtering applies to the live
           Composio catalog (which has its own taxonomy). -->
      <div v-if="!hasSearch" class="kr-toolkit-browser__categories">
        <button
          v-for="cat in categories"
          :key="cat"
          type="button"
          class="kr-toolkit-browser__category"
          :class="{ 'kr-toolkit-browser__category--active': activeCategory === cat }"
          @click="setCategory(cat)"
        >{{ cat }}</button>
      </div>

      <div v-if="hasSearch" class="kr-toolkit-browser__results-header">
        <span class="kr-toolkit-browser__results-count">
          {{ filteredToolkits.length }} {{ filteredToolkits.length === 1 ? 'match' : 'matches' }}
        </span>
        <span v-if="catalogLoading" class="kr-toolkit-browser__loading">
          <i class="el-icon-loading" /> Loading more apps from Composio
        </span>
      </div>

      <div v-if="filteredToolkits.length" class="kr-toolkit-browser__grid">
        <button
          v-for="t in filteredToolkits"
          :key="t.slug"
          type="button"
          class="kr-toolkit-browser__cell"
          :class="{ 'kr-toolkit-browser__cell--connected': isConnected(t.slug) }"
          :title="isConnected(t.slug) ? `${t.name} is already connected` : ''"
          @click="onPick(t)"
        >
          <AutomationToolkitMark :slug="t.slug" :name="t.name" :logo-url="t.logo_url" size="lg" />
          <span class="kr-toolkit-browser__cell-name">{{ t.name }}</span>
          <span class="kr-toolkit-browser__cell-cat">{{ categoryFor(t) }}</span>
          <span
            v-if="isConnected(t.slug)"
            class="kr-toolkit-browser__cell-badge"
            aria-label="Connected"
          >
            <span class="kr-toolkit-browser__cell-dot" />
            Connected
          </span>
        </button>
      </div>
      <div v-else class="kr-toolkit-browser__empty">
        <template v-if="catalogLoading && hasSearch">
          <i class="el-icon-loading" />
          Searching the full Composio catalog.
        </template>
        <template v-else>
          <i class="el-icon-search" />
          No apps match "{{ search }}". Try a different name.
        </template>
      </div>
    </div>

    <div slot="footer" class="kr-toolkit-browser__footer">
      <span class="kr-toolkit-browser__footnote">
        Don't see your tool? Anything with an API connection can be added.
      </span>
      <el-button size="small" @click="dialogVisible = false">Close</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex'
import AutomationToolkitMark from './AutomationToolkitMark.vue'
import { simpleIconUrl } from '@/services/krAutomationDemoBackend'

const e = (slug, name, category) => ({ slug, name, category, logo_url: simpleIconUrl(slug) })

/**
 * Happily-native tile. Renders first in the curated grid because it's first-
 * party and skips OAuth entirely. The logo path matches the brand-memory
 * rule (gold smiley coin, never CSS-recreated). Picking this tile sets
 * toolkit_hint='happily' and routes the composer to the Happily metric
 * catalog (manager reply rate, team DEBI, turnover, recognition, etc.).
 *
 * Name is just "Happily" so the prompt prefix reads "From Happily, ..." —
 * "Happily Data" was redundant.
 */
const HAPPILY_TILE = {
  slug: 'happily',
  name: 'Happily',
  category: 'Happily',
  logo_url: require('@/assets/images/logo_symbol_only.png'),
}

/**
 * Curated tile list shown by default. Hand-picked for category coverage and
 * brand recognition. The Browse modal is hybrid: this list seeds the
 * default view, and the moment the user types in the search box we union it
 * with the live Composio catalog (loaded lazily into Vuex on first open) so
 * any of Composio's 250+ supported apps becomes findable.
 */
const CURATED = [
  HAPPILY_TILE,

  e('hubspot',          'HubSpot',         'Sales & CRM'),
  e('salesforce',       'Salesforce',      'Sales & CRM'),
  e('pipedrive',        'Pipedrive',       'Sales & CRM'),
  e('close',            'Close',           'Sales & CRM'),

  e('linear',           'Linear',          'Engineering'),
  e('github',           'GitHub',          'Engineering'),
  e('gitlab',           'GitLab',          'Engineering'),
  e('jira',             'Jira',            'Engineering'),
  e('sentry',           'Sentry',          'Engineering'),
  e('pagerduty',        'PagerDuty',       'Engineering'),

  e('stripe',           'Stripe',          'Finance'),
  e('quickbooks',       'QuickBooks',      'Finance'),
  e('xero',             'Xero',            'Finance'),
  e('chargebee',        'Chargebee',       'Finance'),

  e('google_analytics', 'Google Analytics','Analytics'),
  e('mixpanel',         'Mixpanel',        'Analytics'),
  e('amplitude',        'Amplitude',       'Analytics'),
  e('segment',          'Segment',         'Analytics'),

  e('notion',           'Notion',          'Productivity'),
  e('google_sheets',    'Google Sheets',   'Productivity'),
  e('airtable',         'Airtable',        'Productivity'),
  e('asana',            'Asana',           'Productivity'),
  e('trello',           'Trello',          'Productivity'),
  e('monday',           'Monday.com',      'Productivity'),

  e('zendesk',          'Zendesk',         'Support'),
  e('intercom',         'Intercom',        'Support'),
  e('freshdesk',        'Freshdesk',       'Support'),
  e('helpscout',        'Help Scout',      'Support'),

  e('slack',            'Slack',           'Communication'),
  e('discord',          'Discord',         'Communication'),
  e('teams',            'Microsoft Teams', 'Communication'),
]

const CATEGORIES = ['All', 'Happily', 'Sales & CRM', 'Engineering', 'Finance', 'Analytics', 'Productivity', 'Support', 'Communication']

const CURATED_BY_SLUG = CURATED.reduce((acc, t) => {
  acc[t.slug] = t
  return acc
}, {})

export default {
  name: 'AutomationToolkitBrowser',
  components: { AutomationToolkitMark },
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
      activeCategory: 'All',
      categories: CATEGORIES,
    }
  },
  computed: {
    ...mapState('goals', {
      fullCatalog: 'toolkitCatalog',
      catalogLoading: 'toolkitCatalogLoading',
    }),
    dialogVisible: {
      get() { return this.visible },
      set(v) { if (!v) this.$emit('close') },
    },
    hasSearch() {
      return this.search.trim().length > 0
    },
    /**
     * The hybrid search.
     *
     * Empty query: just the curated list, filtered by the active category.
     * Non-empty query: union of curated and the live Composio catalog, deduped
     * by slug. Curated entries win on collision so the hand-picked logo and
     * category label are preserved.
     *
     * Sort order during search: curated matches first (familiar names rise to
     * the top), then live-catalog matches alphabetically.
     */
    filteredToolkits() {
      if (!this.hasSearch) {
        return CURATED.filter((t) => this.activeCategory === 'All' || t.category === this.activeCategory)
      }
      const q = this.search.trim().toLowerCase()
      const matchesQuery = (t) =>
        (t.slug || '').toLowerCase().includes(q) || (t.name || '').toLowerCase().includes(q)

      const curatedMatches = CURATED.filter(matchesQuery)
      const seen = new Set(curatedMatches.map((t) => t.slug))

      const liveMatches = (this.fullCatalog || [])
        .filter((t) => !seen.has(t.slug) && matchesQuery(t))
        .sort((a, b) => a.name.localeCompare(b.name))

      return curatedMatches.concat(liveMatches)
    },
  },
  watch: {
    visible(open) {
      if (open) {
        // Fire-and-forget. The action no-ops when the catalog is already hot
        // (loaded within the last hour) so reopening the modal is free.
        this.$store.dispatch('goals/listToolkits')
        // Refresh connection state so the "Connected" badge on each tile is
        // current — important when the picker is reopened after a connection
        // was added in a parallel tab.
        this.$store.dispatch('goals/listConnectedAccounts', {})
        this.$nextTick(() => {
          if (this.$refs.searchInput && this.$refs.searchInput.focus) this.$refs.searchInput.focus()
        })
      } else {
        this.search = ''
        this.activeCategory = 'All'
      }
    },
  },
  methods: {
    setCategory(cat) {
      this.activeCategory = cat
    },
    isConnected(slug) {
      // Happily is first-party and has no OAuth account; don't claim it's
      // "Connected" in the picker because the badge implies a credential.
      if (!slug || slug === 'happily') return false
      return this.$store.getters['goals/hasActiveConnection'](slug)
    },
    /**
     * Returns the human-readable category label for a tile. Curated entries
     * use their hand-assigned bucket. Live-catalog entries fall back to
     * Composio's first category if present, else "More integrations".
     */
    categoryFor(toolkit) {
      const curated = CURATED_BY_SLUG[toolkit.slug]
      if (curated) return curated.category
      const cats = toolkit.categories || []
      if (cats.length && cats[0].name) return cats[0].name
      return 'More integrations'
    },
    onPick(toolkit) {
      this.$emit('pick', toolkit)
      this.dialogVisible = false
    },
  },
}
</script>

<style>
.kr-toolkit-browser .el-dialog__header { padding: 24px 28px 8px; }
.kr-toolkit-browser .el-dialog__body   { padding: 12px 28px 16px; }
.kr-toolkit-browser .el-dialog__footer { padding: 14px 28px 22px; }

/* Brand-color any Element UI primary button inside this sub-dialog so it doesn't
   inherit the legacy teal `[data-v-...] .el-button--primary` rule from goals.vue
   ancestors. `!important` matches the holiday-preset-dialog precedent. */
.kr-toolkit-browser .el-button--primary,
.kr-toolkit-browser .el-button--primary:hover,
.kr-toolkit-browser .el-button--primary:focus,
.kr-toolkit-browser .el-button--primary:active {
  background-color: #6c30cc !important;
  border-color: #6c30cc !important;
  color: #ffffff !important;
}
.kr-toolkit-browser .el-button--primary:hover,
.kr-toolkit-browser .el-button--primary:focus {
  background-color: #522698 !important;
  border-color: #522698 !important;
}
</style>

<style scoped>
.kr-toolkit-browser__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.kr-toolkit-browser__heading {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.kr-toolkit-browser__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.kr-toolkit-browser__categories {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.kr-toolkit-browser__category {
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #4a4a4a;
  background: #fafbfc;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.kr-toolkit-browser__category:hover {
  border-color: #d8c6f0;
  color: #6c30cc;
}

.kr-toolkit-browser__category--active {
  background: #f2ecfb;
  border-color: #6c30cc;
  color: #6c30cc;
}

.kr-toolkit-browser__results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a8a;
}

.kr-toolkit-browser__loading {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  color: #6c30cc;
}

.kr-toolkit-browser__loading .el-icon-loading {
  font-size: 12px;
}

.kr-toolkit-browser__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  max-height: 360px;
  overflow-y: auto;
  padding: 4px 2px 4px 0;
}

.kr-toolkit-browser__cell {
  position: relative;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 8px;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s, background 0.15s;
  text-align: center;
}

.kr-toolkit-browser__cell:hover {
  border-color: #6c30cc;
  background: #faf6ff;
  transform: translateY(-1px);
}

.kr-toolkit-browser__cell--connected {
  border-color: #c8ecd6;
  background: #f3fbf6;
}
.kr-toolkit-browser__cell--connected:hover {
  border-color: #3ac47d;
  background: #ecf8f1;
}

.kr-toolkit-browser__cell-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px 2px 5px;
  border-radius: 999px;
  background: #ecf8f1;
  color: #1f8b5a;
  border: 1px solid #c8ecd6;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1;
}
.kr-toolkit-browser__cell-dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: currentColor;
  display: inline-block;
}

.kr-toolkit-browser__cell-name {
  font-size: 12px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Tile-internal caption. Same eyebrow recipe but at 10px instead of 11px so
   it doesn't compete with the toolkit name. */
.kr-toolkit-browser__cell-cat {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
}

.kr-toolkit-browser__empty {
  text-align: center;
  padding: 32px 0;
  color: #8a8a8a;
  font-size: 12px;
}

.kr-toolkit-browser__empty .el-icon-search,
.kr-toolkit-browser__empty .el-icon-loading {
  display: block;
  font-size: 18px;
  margin-bottom: 6px;
}

.kr-toolkit-browser__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.kr-toolkit-browser__footnote {
  font-size: 11px;
  color: #8a8a8a;
  font-style: italic;
}
</style>
